import * as React from "react";
import { Field, useFormikContext } from "formik";
import { Box, FormHelperText, TextField } from "@mui/material";
import Stack from "@mui/material/Stack";
import { validateEmailFormat } from "../../../utils/stringUtils";

interface Props {
  name: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  variant?: "outlined" | "filled" | "standard";
  helperText?: string;
  validationRegex?: string;
  validationMessage?: string;
}

const PersonInfoInputField: React.FC<Props> = ({
  name,
  label,
  required,
  disabled,
  variant = "standard",
  helperText,
  validationRegex,
  validationMessage
}) => {
  const { values, setFieldValue, setFieldTouched, touched } = useFormikContext<Record<string, string>>();
  const [firstNameError, setFirstNameError] = React.useState<string>();
  const [lastNameError, setLastNameError] = React.useState<string>();
  const [emailError, setEmailError] = React.useState<string>();

  const validate = React.useCallback(
    (value: string | undefined) => {
      // Only validate if the field has been touched
      if (!touched[name]) {
        return undefined;
      }
      const parsedValue = JSON.parse(value || "{}");
      const firstName = parsedValue.firstName;
      const lastName = parsedValue.lastName;
      const email = parsedValue.email;
      if (!required && !firstName && !lastName && !email) {
        setFirstNameError(undefined);
        setLastNameError(undefined);
        setEmailError(undefined);
        return undefined;
      }
      let isValid = true;
      if (!firstName || firstName.trim().length === 0) {
        setFirstNameError("First name is required.");
        isValid = false;
      } else {
        setFirstNameError(undefined);
      }
      if (!lastName || lastName.trim().length === 0) {
        setLastNameError("Last name is required.");
        isValid = false;
      } else {
        setLastNameError(undefined);
      }
      if (!email || !validateEmailFormat(email)) {
        setEmailError("A valid email address is required.");
        isValid = false;
      } else if (validationRegex && !email.match(new RegExp(validationRegex))) {
        setEmailError(validationMessage || "Invalid format");
      } else {
        setEmailError(undefined);
      }
      return isValid ? undefined : "Invalid input";
    },
    [name, required, touched, validationMessage, validationRegex]
  );

  const handleInputChange = (key: string, value: string) => {
    const currentData = JSON.parse(values[name] || "{}");
    const updatedData = { ...currentData, [key]: value };
    setFieldValue(name, JSON.stringify(updatedData));
  };

  const currentData = JSON.parse(values[name] || "{}");

  return (
    <Field
      name={name}
      validate={validate}
      render={() => (
        <Stack>
          {label && (
            <Box>
              <label>{label}</label>
            </Box>
          )}
          <Stack spacing={2} direction="row" alignItems="flex-start">
            <TextField
              label="First Name"
              value={currentData.firstName || ""}
              error={!!firstNameError}
              helperText={firstNameError}
              onChange={e => handleInputChange("firstName", e.target.value)}
              onBlur={() => setFieldTouched(name, true, false)}
              fullWidth
              variant={variant}
              name={name}
              disabled={disabled}
            />
            <TextField
              label="Last Name"
              value={currentData.lastName || ""}
              error={!!lastNameError}
              helperText={lastNameError}
              onChange={e => handleInputChange("lastName", e.target.value)}
              onBlur={() => setFieldTouched(name, true, false)}
              fullWidth
              variant={variant}
              disabled={disabled}
            />
            <TextField
              label="Email"
              value={currentData.email || ""}
              error={!!emailError}
              helperText={emailError}
              onChange={e => handleInputChange("email", e.target.value)}
              onBlur={() => setFieldTouched(name, true, false)}
              fullWidth
              variant={variant}
              disabled={disabled}
            />
          </Stack>
          {helperText && <FormHelperText>{helperText}</FormHelperText>}
        </Stack>
      )}
    />
  );
};

export { PersonInfoInputField };
